import React from 'react'
import { Button } from '../ui/button'
import { 
  Play, 
  Pause, 
  RotateCcw, 
  Download, 
  Trash2, 
  FileText, 
  Scissors,
  AlignHorizontalSpaceAround,
  Eye,
  Plus
} from 'lucide-react'
import { Witness } from '../../store/booksStore'

export interface WitnessAction {
  type: 'preprocess' | 'segment' | 'pause' | 'resume' | 'restart' | 'view' | 'download' | 'delete' | 'split' | 'align' | 'new_run'
  label: string
  icon: React.ComponentType<{ className?: string }>
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  disabled?: boolean
  loading?: boolean
}

interface WitnessActionButtonProps {
  witness: Witness
  action: WitnessAction
  onClick: (action: WitnessAction, witness: Witness) => void
  size?: 'default' | 'sm' | 'lg' | 'icon'
  className?: string
}

const actionIcons = {
  preprocess: FileText,
  segment: Scissors,
  pause: Pause,
  resume: Play,
  restart: RotateCcw,
  view: Eye,
  download: Download,
  delete: Trash2,
  split: Scissors,
  align: AlignHorizontalSpaceAround,
  new_run: Plus
}

export function WitnessActionButton({ 
  witness, 
  action, 
  onClick, 
  size = 'sm',
  className = '' 
}: WitnessActionButtonProps) {
  const IconComponent = action.icon || actionIcons[action.type]
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card click navigation
    if (!action.disabled && !action.loading) {
      onClick(action, witness)
    }
  }

  const getVariant = () => {
    if (action.variant) return action.variant
    
    // Default variants based on action type
    switch (action.type) {
      case 'delete':
        return 'destructive'
      case 'preprocess':
      case 'segment':
        return 'default'
      case 'download':
      case 'view':
        return 'outline'
      default:
        return 'ghost'
    }
  }

  return (
    <Button
      variant={getVariant()}
      size={size}
      onClick={handleClick}
      disabled={action.disabled || action.loading}
      className={`${className} ${action.loading ? 'animate-pulse' : ''}`}
      title={action.label}
    >
      <IconComponent className="h-4 w-4" />
      {size !== 'icon' && (
        <span className="ml-2">{action.label}</span>
      )}
    </Button>
  )
}

// Helper function to get available actions based on witness state
export function getWitnessActions(witness: Witness): WitnessAction[] {
  const actions: WitnessAction[] = []

  switch (witness.processing_status) {
    case 'pending':
      actions.push({
        type: 'preprocess',
        label: 'Start Preprocessing',
        icon: FileText,
        variant: 'default'
      })
      break

    case 'preprocessing':
      actions.push({
        type: 'pause',
        label: 'Pause',
        icon: Pause,
        variant: 'outline'
      })
      break

    case 'preprocessed':
      actions.push({
        type: 'segment',
        label: 'Start Segmentation',
        icon: Scissors,
        variant: 'default'
      })
      if (witness.preprocessing_result) {
        actions.push({
          type: 'view',
          label: 'View Chunks',
          icon: Eye,
          variant: 'outline'
        })
      }
      actions.push({
        type: 'restart',
        label: 'Reset to Pending',
        icon: RotateCcw,
        variant: 'ghost'
      })
      break

    case 'segmenting':
      actions.push({
        type: 'pause',
        label: 'Pause Processing',
        icon: Pause,
        variant: 'outline'
      })
      break

    case 'completed':
      // Only show "View Results" if segmentation_result exists
      if (witness.segmentation_result) {
        actions.push({
          type: 'view',
          label: 'View Results',
          icon: Eye,
          variant: 'outline'
        })
        actions.push({
          type: 'new_run',
          label: 'New Segmentation Run',
          icon: Plus,
          variant: 'default'
        })
        actions.push({
          type: 'download',
          label: 'Download Results',
          icon: Download,
          variant: 'outline'
        })
      } else {
        // If completed but no results, offer to re-segment
        actions.push({
          type: 'segment',
          label: 'Re-run Segmentation',
          icon: Scissors,
          variant: 'default'
        })
      }
      actions.push({
        type: 'restart',
        label: 'Reset to Pending',
        icon: RotateCcw,
        variant: 'ghost'
      })
      break

    case 'error':
      actions.push({
        type: 'restart',
        label: 'Retry',
        icon: RotateCcw,
        variant: 'default'
      })
      break

    case 'paused':
      actions.push({
        type: 'resume',
        label: 'Resume',
        icon: Play,
        variant: 'default'
      })
      actions.push({
        type: 'restart',
        label: 'Restart',
        icon: RotateCcw,
        variant: 'outline'
      })
      break
  }

  // Always available actions
  actions.push({
    type: 'delete',
    label: 'Delete Witness',
    icon: Trash2,
    variant: 'destructive'
  })

  return actions
}